<script setup>
import 'devextreme-vue/text-area'
import moment from 'moment'
import { ref, computed, watch } from 'vue'
import { menuRoutesEnum } from '@/router'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxSplitter, DxItem } from 'devextreme-vue/splitter'
import { usePartialSave } from '@/composables/partial-save'
import { useDialog } from '@/composables/dialog'
import { useAttachment } from '@/composables/registry/service-attachment-grid'

import * as dataGrid from '@/composables/data-grid'

import { useValidationTabs } from '@/composables/validation'
import { useNotify } from '@/composables/notify'
import { usePrint } from '@/composables/print'
import { clearRemovedRoundingRowFlag } from '@/composables/rounding-row'
import { useDialogTitle } from '@/composables/garage/quote-grid'
import { useColumns } from '@/composables/garage/quote-grid'
import { useAddressRef, useLabelController } from '@/composables/data-grid-column'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useGarageStore } from '@/stores/garage.store'
import { useDocumentIdentifiers } from '@/composables/document'
import { useInfoBikeSearch } from '@/composables/registry/info-bike-search'

import { registryApi } from '@/apis/registry.api'

import AttachmentIconList from '@/components/shared/AttachmentIconList.vue'
import CarServiceNumberBox from '@/components/shared/CarServiceNumberBox.vue'
import CityDropDownBox from '@/components/shared/CityDropDownBox.vue'
import CountryDropDownBox from '@/components/shared/CountryDropDownBox.vue'
import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import DocumentGenerateDialog from '@/components/shared/DocumentGenerateDialog.vue'
import DocumentTagsTagBox from '@/components/shared/DocumentTagsTagBox.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import InfoBikeSearchCaptionButton from '@/components/shared/InfoBikeSearchCaptionButton.vue'
import InfoBikePlateNumberEditor from '@/components/shared/InfoBikePlateNumberEditor.vue'
import MechanicDropDownBox from '@/components/shared/MechanicDropDownBox.vue'
import MechanicsAvatarList from '@/components/shared/MechanicsAvatarList.vue'
import PrintDialog from '@/components/shared/PrintDialog.vue'
import QuoteRowGrid from '@/components/garage/QuoteRowGrid.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import StateDropDownBox from '@/components/shared/StateDropDownBox.vue'
import ValidationTab from '@/components/shared/ValidationTab.vue'
import VehicleDropDownBox from '@/components/shared/VehicleDropDownBox.vue'
import InfoTooltipIcon from '@/components/shared/InfoTooltipIcon.vue'

import { useDocumentService } from '@/services/document.service'
import { useSubjectService } from '@/services/subject.service'
import { useEntityLock } from '@/services/entity-lock.service'

const props = defineProps({
  selectMode: {
    type: Boolean,
    default: false,
  }
})

const gridId = 'quotes-grid'
const formInstance = ref(null)
const mainSplitterId = 'quote-grid-main-splitter'

const { route, router } = dataGrid.useGridRouter()

const i18n = getI18n()
const registryStore = useRegistryStore()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()
const garageStore = useGarageStore()
const { genericDialog } = useDialog()
const { currentCountryId, currentVatCountryId: currentCustomerVatCountryId, currentStateId, currentZipCode, currentFilteredCitiesCount, setCellValueCity, setCellValueZipCode, onCountryChanged, onStateChanged } = useAddressRef({ onVatCountryChanged: () => updateFormLabels(editors, subjectLabelFields) })
const currentCustomerVatCountryCode = computed(() => {
  const country = webSiteStore.countries.find(x => x.id === currentCustomerVatCountryId.value)
  return country ? country.iso3_code : null
})
const { notifyError } = useNotify()

const { partialSave, isSaveDisabled, onRowValidating } = usePartialSave()
const { getAttachmentEntities } = useAttachment()
const { extractReferenceData } = useDocumentService()
const { addWarning: addDocumentWarning, displayWarnings: displayGenerateDocumentWarnings, checkPartialFields } = dataGrid.useGenerateDocumentWarning()

const {
  gridRef,
  refreshGridAsync,
  focusGrid,
  onFocusedRowChangedDebounced: onFocusedRowChanged,
} = dataGrid.useGridRef()
const { isVatCountryIta } = useSubjectService()
const { isVehicleSaveDisabled, onDocumentInitNewRowAsync, loadDocumentDetailsAsync, onDocumentEditorPreparing, onDocumentSaving, onDocumentRowUpdating, getDocumentTags } = useDocumentIdentifiers()
watch(() => webSiteStore.userDataFilter, () => refreshGridAsync())

const entityLock = useEntityLock({
  baseLockUrl: garageStore.api.getAbsoluteUrl() + garageStore.api.quotesUrl,
  modelName: 'Quote',
  gridRef,
})

const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    edit: (e) => !e.isReadonly && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row) && !entityLock.isLocked(false, e.entityId),
    delete: (e) => !e.isReadonly && !(e.data?.has_rounding_expense_row || e.data?.has_rounding_row) && !entityLock.isLocked(false, e.entityId),
    view: (e) => e.isReadonly || e.data?.has_rounding_expense_row || e.data?.has_rounding_row || entityLock.isLocked(false, e.entityId)
  }
})

defineExpose({ focusedRow: dataGrid.focusedRow })
const currentCustomerTypeId = ref(null)
const currentCustomerSubTypeId = ref(null)
const currentVehicleFuelType = ref(null)
const selectedDocument = ref(null)
const editors = {}
const generateDocumentType = ref(null)
const isPaid = ref(false)
const selectedInterventionTypeId = ref(null)
const includesVat = ref(false)
const currentCarService = ref(null)
const vehicleSetValue = ref(null)

const isOwnerSaveDisabled = ref(true)
const isChangeOwnerDisabled = ref(true)

const print = usePrint()
const isReadOnly = ref(false)

let firstEditLoad = false
let worksheetToolbarButton = null
const worksheetGenerateDialogVisible = ref(false)

const refDocument = ref(null)
const selectedRows = ref([])

const { validationTabIndexErrors } = useValidationTabs()

const disabledOwnerFields = ['subject_type_ref', 'subject_sub_type_ref', 'name', 'mobile']
const addressRequiredFields = ['country', ['state', 'state_text'], 'zip_code', ['city', 'city_text'], 'street']
const excludedReadOnlyFields = ['expected_delivery_date']

const { customizeItem, updateFormLabels, registerLabelEditors } = useLabelController({ vehicleFuelType: currentVehicleFuelType, subjectType: currentCustomerTypeId, subjectSubTypeId: currentCustomerSubTypeId, vatCountryId: currentCustomerVatCountryId })
const subjectLabelFields = ['customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref', 'customer.vat_number', 'customer.tax_code']
const {
  infoBikeSearchDisabled, showPulse, pulseCount,
  currentInfoBikeBrandCode, currentInfoBikeModelCode, extraData,
  disableInfoBikeSearch, onPlateBlur, infoBikeSearch, loadCreditsAsync,
  onInfoBikeEditorPreparing, infoBikeFormGroup
} = useInfoBikeSearch({
  getEditor: (field) => editors[field],
  getEditingData: () => gridRef.value?.instance?.getVisibleRows().find(row => row.isEditing)?.data,
  setVehicle: (vehicle, options) => vehicleSetValue.value?.(options?.preserveOwner ? { ...vehicle, preserveOwner: true, preservedCustomer: options.customer, preservedCustomerId: options.customerId } : vehicle),
  beginLoading: () => gridRef.value?.instance?.beginCustomLoading(),
  endLoading: () => gridRef.value?.instance?.endCustomLoading(),
  registryStore,
  garageStore,
  portalStore
})

const allComputedTags = computed(() => {
  return getDocumentTags(isPaid, includesVat, selectedInterventionTypeId)
})

const columns = useColumns(
  editors,
  currentCountryId,
  currentStateId,
  currentCustomerTypeId,
  currentCustomerSubTypeId,
  currentVehicleFuelType,
  formInstance,
  isChangeOwnerDisabled,
  includesVat,
  selectedInterventionTypeId,
  currentCustomerVatCountryId,
  currentCustomerVatCountryCode,
  currentCarService,
  currentInfoBikeBrandCode,
  currentInfoBikeModelCode,
  setCellValueZipCode,
  setCellValueCity
)

const { dialogTitle } = useDialogTitle()

const currency = computed(() => {
  if (!selectedDocument.value) return null
  return webSiteStore.currencies.find(i => i.id === selectedDocument.value.currency_ref)
})

const onInitializedCustom = (e) => {
  disableInfoBikeSearch()
  if (isReadOnly.value && !dataGrid.isAdding.value) {
    const toolbarItems = e.component.option('toolbarItems') || []

    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return {
        ...item,
        visible: false,
      }
    })

    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    entityLock.readOnlyToolbarItems(e)
  }
}

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 4,
  items: [
    {
      itemType: 'group',
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.quoteData'),
          name: 'mdi-wrench',
          captionTemplate: 'generic-caption-template',
          colCount: 4,
          items: [
            { dataField: 'date', colSpan: 2 },
            { dataField: 'series', editorOptions: { readOnly: true } },
            { dataField: 'number_mnemonic', editorOptions: { readOnly: true } },
            webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ) ? {
              dataField: 'worksheet.id',
              colSpan: 4,
              label: { text: i18n.t('common.worksheet') }, editorOptions: { readOnly: true }
            } : {},
            { dataField: 'garage_reason_ref', colSpan: 3 },
            { dataField: 'car_service' },
            { dataField: 'deposit', colSpan: 2 },
            { dataField: 'garage', colSpan: 2 },
            { dataField: 'mechanics', colSpan: 2 },
            { dataField: 'expected_delivery_date', colSpan: 2 },
            { dataField: 'payment_type_ref', colSpan: 2 },
            { dataField: 'includes_vat', colSpan: 2 },
            { dataField: 'discount', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions() }},
            { dataField: 'worksheet.paid_amount', colSpan: 2, editorOptions: { ...dataGrid.useCurrencyOptions({ currency: currency.value || webSiteStore.getDefaultCurrency() }), min: 0, readOnly: true }},
            { dataField: 'currency_ref', colSpan: 2 },
            { dataField: 'exchange_rate', colSpan: 2, editorOptions: { readOnly: true } },
            { itemType: 'empty', colSpan: 2 },
            { dataField: 'exchange_rate_date', colSpan: 2, editorOptions: { readOnly: true } },
          ]
        },
        {
          itemType: 'group',
          caption: i18n.t('common.documentStatus'),
          name: 'mdi-wrench',
          captionTemplate: 'generic-caption-template',
          colCount: 4,
          items: [
            {
              dataField: 'tags',
              colSpan: 4,
              tagTemplate: 'tagTemplate'
            }
          ]
        },
      ]
    },
    {
      itemType: 'tabbed',
      colSpan: 3,
      cssClass: 'form-right-section',
      tabs: [
        {
          title: i18n.t('common.mainInfo'),
          items: [
            infoBikeFormGroup(i18n, garageStore),
            {
              itemType: 'group',
              caption: i18n.t('common.vehicleInfo'),
              name: 'mdi-motorbike',
              captionTemplate: 'vehicle-info-caption-template',
              validationGroup: 'vehicleGroup',
              colSpan: 2,
              colCount: 8,
              items: [
                { dataField: 'vehicle_id', colSpan: 2},
                { dataField: 'vehicle.supplier_brand_ref', label: { text: i18n.t('common.brandAlt') } },
                { dataField: 'vehicle.plate_number' },
                { dataField: 'vehicle.vin', editorOptions: { maxLength: 17 }, colSpan: 2 },
                { dataField: 'vehicle.infoBikeCodeFull', colSpan: 2 },
                { dataField: 'vehicle.name', colSpan: 2 },
                { dataField: 'vehicle.color', colSpan: 2 },
                { dataField: 'vehicle.km', editorOptions: { min: 0 } },
                { dataField: 'vehicle.engine_size', editorOptions: { min: 0 } },
                { dataField: 'vehicle.next_revision_due_date', colSpan: 2 },
              ]
            },
            {
              itemType: 'group',
              colCount: 3,
              colSpan: 3,
              cssClass: 'worksheet-owner-ctn',
              items: [
                {
                  itemType: 'group',
                  caption: i18n.t('common.ownerInfo'),
                  name: 'mdi-account-circle',
                  captionTemplate: 'owner-info-caption-template',
                  validationGroup: 'ownerGroup',
                  colSpan: 2,
                  colCount: 3,
                  items: [
                    { dataField: 'customer_id' },
                    { dataField: 'customer.subject_type_ref' },
                    { dataField: 'customer.subject_sub_type_ref' },
                    { dataField: 'customer.name', label: { text: i18n.t('common.nameAlt') } },
                    { dataField: 'customer.name_ext', label: { text: i18n.t('common.nameExtAlt') } },
                    { itemType: 'empty' },
                    ...dataGrid.useDefaultAddressEditing('customer.address', 1, null, {}, true),
                    { dataField: 'customer.email', label: { text: i18n.t('common.email') } },
                    { dataField: 'customer.mobile', label: { text: i18n.t('common.mobile') }, editorOptions: { onContentReady: (e) => { dataGrid.usePhoneMask(e.element, { definitions: dataGrid.useMask().mobileMaskRules }) } } },
                    { itemType: 'empty' },
                    { dataField: 'customer.vat_country_id', label: { text: i18n.t('common.vatCountry')} },
                    { dataField: 'customer.vat_number', label: { text: i18n.t('common.vatNumber')} },
                    { dataField: 'customer.tax_code', label: { text: i18n.t('common.taxCode') } },
                  ]
                },
                {
                  itemType: 'group',
                  caption: i18n.t('common.customerReport', 2),
                  name: 'mdi-notebook-edit',
                  captionTemplate: 'generic-caption-template',
                  items: [
                    {
                      dataField: 'notes',
                      editorType: 'dxTextArea',
                      editorOptions: { height: '20rem' }
                    },
                  ]
                },
              ]
            },
          ]
        },
        {
          title: i18n.t('common.billing'),
          tabTemplate: 'validationTabTemplate',
          items: [
            {
              itemType: 'group',
              caption: i18n.t('common.fiscalData'),
              name: 'mdi-receipt-text-outline',
              captionTemplate: 'generic-caption-template',
              colSpan: 3,
              colCount: 4,
              items: [
                { dataField: 'vat_type_ref'},
                { dataField: 'currency_ref' },
                { dataField: 'exchange_rate', editorOptions: { readOnly: true } },
                { dataField: 'exchange_rate_date', editorOptions: { readOnly: true } },
              ]
            }
          ]
        },
      ]
    }
  ],
  onInitialized: (e) => { formInstance.value = e.component },
  customizeItem: customizeItem,
}, {
  allowAdding: !props.selectMode,
  allowUpdating: (e) => !props.selectMode && !(e.data?.has_rounding_row || e.data?.has_rounding_expense_row),
  allowDeleting: (e) => !props.selectMode && !(e.data?.has_rounding_row || e.data?.has_rounding_expense_row),
  onInitializedCustom: (e) => onInitializedCustom(e),
  entityLock: entityLock
})

const onInitNewRow = async(e) => {
  firstEditLoad = true
  e.data.date = new Date()
  e.data.series = e.data.date.getFullYear().toString()
  e.data.status = 0

  isPaid.value = e.data?.sale_document?.paid
  currentVehicleFuelType.value = null
  currentCustomerTypeId.value = null
  currentCustomerSubTypeId.value = null
  currentInfoBikeModelCode.value = null

  if (registryStore.companyData.deposit) {
    e.data.deposit = registryStore.companyData.deposit
  }
  if (registryStore.companyData.garage) {
    e.data.garage = registryStore.companyData.garage
  }
  if (registryStore.companyData.default_currency_ref) {
    e.data.currency_ref = registryStore.companyData.default_currency_ref

    const currentCurrency = webSiteStore.currencies.find((currency) => currency.id === registryStore.companyData.default_currency_ref)
    if (currentCurrency) {
      e.data.exchange_rate = currentCurrency.exchange_rate
      e.data.exchange_rate_date = currentCurrency.exchange_rate_date
    }
  }

  if (registryStore.companyData?.default_quote_garage_reason_ref) {
    e.data.garage_reason_ref = registryStore.companyData.default_quote_garage_reason_ref
  } else {
    e.data.garage_reason_ref = registryStore.companyData.default_quote_portal_garage_reason_ref
  }
  selectedInterventionTypeId.value = garageStore.garageReasons.find(i => i.id === e.data.garage_reason_ref)?.intervention_type_ref

  e.promise = (async() => { await onDocumentInitNewRowAsync(e, garageStore.api.getQuoteNextIdByDataAsync) })()
  e.data.includes_vat = includesVat.value = e.data.includes_vat ?? false

  await loadCreditsAsync()
}

const readOnlyFields = [
  'vehicle.vin', 'vehicle.plate_number', 'vehicle.engine_code',
  'vehicle.color', 'vehicle.engine_size', 'vehicle.name',
  'vehicle.supplier_brand_ref'
]

const makeFieldsReadOnly = (e, fields, readOnlyValue) => {
  if (e.parentType === 'dataRow' && fields.includes(e.dataField)) {
    e.editorOptions.disabled = !!readOnlyValue
  }
}

const onEditorPreparing = (e) => {
  if (firstEditLoad) {
    if (e.row?.data?.id) {
      firstEditLoad = false
      e.promise = loadDocumentDetailsAsync(garageStore.api.getQuoteDetailsAsync, e.row, {
        currentVehicleFuelType,
        currentCustomerTypeId,
        currentCountryId,
        currentCustomerVatCountryId,
        currentStateId,
        includesVat,
        currentCarService,
        formInstance
      })
    } else {
      if (e.parentType === 'dataRow' && e.dataField === 'vehicle.vin') {
        firstEditLoad = false
        e.editorOptions.onInitialized = (e) => { setTimeout(() => { e.component.focus() }, 500) }
      }
    }
  }

  if (e.parentType === 'dataRow' && (e.dataField === 'customer.name' || e.dataField === 'customer.name_ext' || e.dataField === 'customer.subject_type_ref' || e.dataField === 'customer.subject_sub_type_ref' ||
      e.dataField === 'customer.vat_country_id' || e.dataField === 'customer.vat_number' || e.dataField === 'customer.tax_code' || e.dataField === 'customer.address.zip_code' || e.dataField === 'customer.address.street' ||
      e.dataField === 'customer.address.state'
  )) {
    e.editorOptions.disabled = !!e.row.data?.customer?.id || !!e.row.data?.customer_id
  }
  if (e.parentType === 'dataRow' && e.dataField === 'worksheet.id') {
    e.editorOptions.visible = e.row.data?.worksheet
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.vat_number') {
    e.editorOptions.onInitialized = (e) => { editors['customer.vat_number'] = e.component }
    e.editorOptions.disabled = !isVatCountryIta(currentCustomerVatCountryId.value) || e.row?.data?.id || e.row?.data?.customer?.id
  }
  if (e.parentType === 'dataRow' && e.dataField === 'customer.tax_code') {
    e.editorOptions.onInitialized = (e) => { editors['customer.tax_code'] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'customer.subject_type_ref') {
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      currentCustomerTypeId.value = args.value
      currentCustomerSubTypeId.value = null
      defaultValueChanged(args)
      updateFormLabels(editors, subjectLabelFields)
    }
    e.editorOptions.onInitialized = (e) => { editors['customer.subject_type_ref'] = e.component }
  }
  registerLabelEditors(e, editors, ['customer.name', 'customer.name_ext', 'customer.subject_sub_type_ref'])
  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.next_revision_due_date') {
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      args.value = args.value && moment(args.value).format(dataGrid.useMask().dateSimpleFormat)
      defaultValueChanged(args)
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'car_service') {
    e.editorOptions.onInitialized = (e) => { editors['car_service'] = e.component }

    const garageReason = garageStore.garageReasons.find(i => i.id === e.row.data?.garage_reason_ref)
    const interventionType = garageStore.interventionTypes.find(i => i.id === garageReason?.intervention_type_ref)

    e.editorOptions.disabled = !interventionType?.has_car_service
  }

  onDocumentEditorPreparing(e, editors, garageStore.api.getQuoteNextIdByDataAsync, gridRef, excludedReadOnlyFields, portalStore.documentTypesEnum.GARAGE_QUOTE)

  if (e.row?.data?.has_worksheet && e.parentType === 'dataRow') {
    e.editorOptions.readOnly = e.row?.data?.has_worksheet
  }
  if (e.row?.data?.vehicle?.id && e.parentType === 'dataRow') {
    makeFieldsReadOnly(e, readOnlyFields, e.row.data?.vehicle?.id)
  }
  if (e.parentType === 'dataRow' && (e.dataField === 'customer.subject_type_ref' || e.dataField === 'customer.subject_sub_type_ref' || e.dataField === 'customer.name' || e.dataField === 'customer.name_ext' ||
                                      e.dataField === 'customer.address.zip_code' || e.dataField === 'customer.address.street' || e.dataField === 'customer.address.state' ||
                                      e.dataField === 'customer.address.country' || e.dataField === 'customer.address.city') ||
                                      e.dataField === 'customer.mobile' || e.dataField === 'customer.tax_code') {
    isOwnerSaveDisabled.value = isSaveDisabled('customer', disabledOwnerFields, gridRef, addressRequiredFields)
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      isOwnerSaveDisabled.value = isSaveDisabled('customer', disabledOwnerFields, gridRef, addressRequiredFields)
    }
  }

  onInfoBikeEditorPreparing(e, editors)

  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.supplier_brand_ref') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.supplier_brand_ref'] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.infoBikeCodeFull') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.infoBikeCodeFull'] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.name') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.name'] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.engine_size') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.engine_size'] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.vin') {
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onInitialized = (args) => {
      editors['vehicle.vin'] = args.component
    }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      let newValue = args.value || ''
      newValue = newValue.toUpperCase()
      args.component.option('value', newValue)
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vehicle.plate_number') {
    e.editorOptions.onInitialized = (e) => { editors['vehicle.plate_number'] = e.component }
    const defaultValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      let newValue = args.value || ''
      newValue = newValue.toUpperCase()
      if (!registryStore.companyData.vehicle_plate_allow_blank_spaces) {
        newValue = newValue.replace(/\s+/g, '')
      }
      args.component.option('value', newValue)
    }
  }
}

const onDocumentGenerated = async() => {
  selectedDocument.value = await repaintRow(gridRef, garageStore.api.getQuoteByIdAsync, selectedDocument.value?.id, selectedDocument.value?.id)
  gridRef.value?.instance?.clearSelection()
  await refreshGridAsync()
}

const onRowInserting = (e) => {
  if (e.data?.customer?.id) {
    delete e.data.customer.code
  }
}

const onRowRemoving = (e) => {
  selectedDocument.value = null
}

const onRowUpdating = (e) => {
  if (e.newData?.customer?.id) {
    delete e.newData.customer.code
  }
  if (e.newData?.customer && !e.newData.customer.id) {
    e.newData.customer.id = e.newData.customer_id = e.oldData.customer.id
  }
  if (e.newData?.vehicle && !e.newData.vehicle.id) {
    e.newData.vehicle.id = e.newData.vehicle_id = e.oldData.vehicle.id
  }
  onDocumentRowUpdating(e, selectedDocument)
}

const saveNewOwner = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  const owner = editingRow.data?.customer

  if (!owner || owner.id || !owner.subject_sub_type_ref || !owner.subject_type_ref || !owner.name) {
    grid.getController('validating')?.validate(false)
    return
  }

  const nextIdResponse = await registryStore.api.getSubjectNextIdAsync()
  owner.code = nextIdResponse.data
  owner.type = registryStore.subjectEntityTypes.CUSTOMER
  owner.customer ??= {}
  owner.customer.discount_list_ref = registryStore.getLocalizedDiscountLists()[0]?.id
  delete owner.id
  delete owner.address

  const res = await partialSave(owner, registryApi.createSubjectAsync, { grid: grid })
  if (res?.customer) {
    await registryStore.fetchCustomersAsync(true)
    const customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === res.customer.id)
    grid.cellValue(rowIndex, 'customer_id', customer)
    formInstance.value?.repaint()
  }
}

const saveNewVehicle = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  const vehicle = editingRow.data?.vehicle
  const customer = editingRow.data?.customer

  if (!vehicle || vehicle.id || !customer || !customer.id || (!vehicle.vin && !vehicle.plate_number) || !vehicle.name || !vehicle.supplier_brand_ref) {
    grid.getController('validating')?.validate(false)
    return
  }

  const nextIdResponse = await registryStore.api.getVehicleNextIdAsync()
  vehicle.id = nextIdResponse.data
  vehicle.status = registryStore.vehicleStatusTypesEnum.TRANSITED
  vehicle.customer_id = customer.id

  const res = await partialSave(vehicle, registryApi.createVehicleAsync, { grid: grid })
  if (res) {
    await registryStore.fetchVehiclesAsync(true)
    grid.cellValue(rowIndex, 'vehicle_id', res)
    formInstance.value?.repaint()
  }
}

const addNewVehicleOwner = async() => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const vehicle_id = editingRow.data?.vehicle_id
  const customer_id = editingRow.data?.customer_id

  if (!vehicle_id || !customer_id) {
    grid.getController('validating')?.validate(false)
    return
  }

  const newMovement = webSiteStore.newVehicleOwnerMovement(customer_id, vehicle_id)

  genericDialog(
    i18n.t('common.update'),
    i18n.t('common.updateOwner'),
    { cancel: true, persistent: true }
  ).onOk(async() => {
    await registryApi.createVehicleMovementAsync(newMovement)
    await registryStore.fetchVehiclesAsync(true)

    isChangeOwnerDisabled.value = true
  })
}

const onVehicleChanged = async(e, cellInfo) => {
  const customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === e.value?.customer_id)
  currentCountryId.value = customer?.address?.country || null
  currentCustomerVatCountryId.value = customer?.vat_country_id || null

  if (cellInfo.data?.infoBike && e.value?.plate_number !== cellInfo?.data?.infoBike?.plate_number) {
    cellInfo.data.infoBike.plate_number = null
  }

  currentInfoBikeBrandCode.value = null
  currentInfoBikeModelCode.value = null
  editors['infoBike.brand']?.option('value', null)
  editors['infoBike.model']?.option('value', null)
  editors['infoBike.variant']?.option('value', null)

  formInstance.value?.repaint()
}

const onEditingStart = async(e) => {
  firstEditLoad = true
  includesVat.value = e.data.includes_vat
  isPaid.value = e.data?.sale_document?.paid
  if (e.data?.worksheet?.id || e.data?.has_rounding_expense_row || e.data?.has_rounding_row) {
    isReadOnly.value = true
    return
  }
  if (e.data?.customer?.address?.country) {
    currentCountryId.value = e.data.customer.address.country
  }
  if (e.data?.customer?.vat_country_id) {
    currentCustomerVatCountryId.value = e.data.customer.vat_country_id
  }
  if (e.data?.customer?.address?.state) {
    currentStateId.value = e.data.customer.address.state
  }
  if (e.data?.customer?.subject_type_ref) {
    currentCustomerTypeId.value = e.data.customer.subject_type_ref
  }
  if (e.data?.customer?.subject_sub_type_ref) {
    currentCustomerSubTypeId.value = e.data.customer.subject_sub_type_ref
  }
  isChangeOwnerDisabled.value = (e.data?.customer_id === e.data?.vehicle?.customer_id || !e.data?.vehicle_id)
  if (e.data?.garage_reason_ref) {
    const currentGarageReason = garageStore.garageReasons.find(i => i.id === e.data?.garage_reason_ref)
    selectedInterventionTypeId.value = currentGarageReason?.intervention_type_ref
  }
  await loadCreditsAsync()
}

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, {
    allowBatchDelete: true,
    addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.quote')}` }
  })

  e.toolbarOptions.items.unshift(
    print.printToolbarWidget({ onClick: async() => {
      try {
        const filename = selectedRows.value.length === 1 ? i18n.t('common.quote') + '.pdf' : i18n.t('common.quote', 2) + '.zip'
        await garageStore.api.printQuotesAsync(filename, {filename: filename, ids: selectedRows.value.map(i => i.id), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone}, { skipErrorMessage: (e) => (e.response?.data?.warnings) })
      } catch (err) {
        handleWarningMessages(err)
      }
    }}),
    print.printOptionsToolbarWidget()
  )

  if (webSiteStore.hasClaim(webSiteStore.claimsEnum.WORKSHOP_WORKSHEET_READ)) {
    e.toolbarOptions.items.unshift(
      {
        location: 'after',
        widget: 'dxDropDownButton',
        options: {
          icon: 'textdocument',
          text: `${i18n.t('common.generate')}...`,
          disabled: true,
          useSelectMode: false,
          displayExpr: 'text',
          dropDownOptions: { width: '9rem' },
          onItemClick: async(e) => {
            if (selectedRows.value && selectedRows.value[0]) {
              const generatingItem = (await (garageStore.api.getQuoteByIdAsync(selectedRows.value[0].id)))?.data
              if (!generatingItem) {
                notifyError(i18n.t('error.item_not_found'))
                return
              }

              generateDocumentType.value = e.itemData.id
              const docName = `${i18n.t('common.quote')} ${generatingItem.series} - ${generatingItem.number_mnemonic}`

              if (selectedRows.value.some(row => row.worksheet)) { addDocumentWarning(docName, i18n.t('error.validation.related_document_already_exists')) }
              if (selectedRows.value.some(row => row.rows_count === 0)) { addDocumentWarning(docName, i18n.t('error.validation.no_related_rows_found')) }

              await checkPartialFields(generateDocumentType.value, docName, selectedRows.value, addDocumentWarning)

              if (!displayGenerateDocumentWarnings()) {
                refDocument.value = structuredClone(generatingItem)
                refDocument.value.generating_document_date = refDocument.value?.date
                refDocument.value.date = moment(new Date()).format('YYYY-MM-DD')
                refDocument.value.worksheet_number_mnemonic = ''
                refDocument.value.car_service = (await registryStore.api.getVehicleNextCarServiceAsync(refDocument.value.vehicle_id))?.data || currentCarService.value || null
                worksheetGenerateDialogVisible.value = true
              }
            }
          },
          items: [{ id: portalStore.documentGenerateEnum.WORKSHEET, text: i18n.t('common.worksheet'), icon: 'mdi mdi-wrench' }],
          onInitialized: (e) => { worksheetToolbarButton = e.component }
        },
      }
    )
  }

  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const onEditCanceled = (e) => {
  isReadOnly.value = false
}

const batchDeleteAsync = async(selectedIds) => {
  await garageStore.api.batchDeleteQuotesAsync(selectedIds)
  selectedDocument.value = null
}

const onSaving = (e) => {
  e.promise = (async() => { await onDocumentSaving(e, selectedDocument, { isOwnerSaveDisabled: isOwnerSaveDisabled, isVehicleSaveDisabled: isVehicleSaveDisabled }) })()
}

const onSaved = async(e) => { await refreshGridAsync() }

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  print.printToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
  print.printOptionsToolbarButton.value.option('disabled', e.selectedRowsData.length === 0)
  worksheetToolbarButton.option('disabled', e.selectedRowsData.length !== 1 || e.selectedRowsData.some(row => entityLock.isLocked(false, row.id)))
}

const onFocusedRowChangedDebounced = async(e) => onFocusedRowChanged(e, selectedDocument)

const { repaintRow } = dataGrid.useUpdateMasterDetail()

const onDetailRowSaved = async(parentId, removedRow) => {
  clearRemovedRoundingRowFlag(selectedDocument.value, removedRow)
  const updatedDocument = await repaintRow(gridRef, garageStore.api.getQuoteByIdAsync, parentId, parentId)
  selectedDocument.value ? Object.assign(selectedDocument.value, updatedDocument) : selectedDocument.value = updatedDocument
  isReadOnly.value = !!updatedDocument?.has_worksheet || !!updatedDocument?.worksheet?.id || !!updatedDocument?.has_rounding_row || !!updatedDocument?.has_rounding_expense_row
}

const onDetailContentReady = (e) => gridRef.value.instance.updateDimensions()

const onClearFilter = () => {
  router.replace()
}

const onSplitterResize = (e, splitterId) => {
  const totalSize = e.component.option('orientation') === 'vertical'
    ? e.component.element().offsetHeight
    : e.component.element().offsetWidth

  const sizes = e.component.option('items').map(item => {
    const px = parseFloat(item.size)
    const percentage = ((px / totalSize) * 100).toFixed(2) + '%'
    return { size: percentage }
  })
  webSiteStore.setSplitterState(splitterId, sizes)

  if (splitterId === mainSplitterId && selectedDocument.value && gridRef.value?.instance) {
    const gridInstance = gridRef.value.instance

    gridInstance.updateDimensions()
    gridInstance.repaint()

    const rowIndex = gridInstance.getRowIndexByKey(selectedDocument.value.id)
    if (rowIndex >= 0) {
      const rowHeight = gridInstance.option('rowHeight') || 37
      const scrollTop = rowIndex * rowHeight

      const scrollable = gridInstance.getScrollable()
      if (scrollable) {
        const currentScroll = scrollable.scrollOffset()
        scrollable.scrollTo({ x: currentScroll.left, y: scrollTop })
        gridInstance.repaint()
      }
    }
  }
}

const restoreSplitterSize = (e, splitterId) => {
  const saved = webSiteStore.splitterStates[splitterId]
  if (saved) {
    try {
      saved.forEach((item, index) => {
        e.component.option(`items[${index}].size`, item.size)
      })
    } catch (err) {
      console.warn(`Splitter "${splitterId}": data error.`)
    }
  }
}

const formEditors = [
  { dataField: 'car_service', label: { text: i18n.t('common.carServiceNumber')}},
]

let gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataGrid.useODataSource(garageStore.api.getAbsoluteUrl() + garageStore.api.quotesUrl),
  columns: props.selectMode ? columns.filter(i => i.dataField !== 'worksheet.id') : columns,
  onClearFilter: onClearFilter,
  focusGrid: focusGrid,
  splittedGrid: true,
})

if (!props.selectMode) {
  gridOptions = {...gridOptions, ...{
    editing: editing,
    selection: dataGrid.useDefaultGridSelection(),
    onInitNewRow: onInitNewRow,
    onEditingStart: onEditingStart,
    onRowInserting: onRowInserting,
    onRowUpdating: onRowUpdating,
    onRowValidating: (e) => onRowValidating(e, gridRef, true, extraData),
    onEditorPreparing: onEditorPreparing,
    onToolbarPreparing: onToolbarPreparing,
    onSelectionChanged: onSelectionChanged,
    onFocusedRowChangedDebounced: onFocusedRowChangedDebounced,
    onEditCanceled: onEditCanceled,
    onSaving: onSaving,
    onSaved: onSaved,
    onRowRemoving: onRowRemoving,
    routerFilter: {
      fields: [{series: 'series'}, {number: 'number_mnemonic'}],
      getRoute: () => route,
      router: router
    }
  }}
}

</script>
<template>
  <div>
    <print-dialog
      v-model:visible="print.printDialogVisible.value"
      :selected-rows="selectedRows"
      :entity="portalStore.printEntities.QUOTE"
      :print-function="garageStore.api.printQuotesAsync"
      :print-preview-function="(options) => garageStore.api.printPreviewQuoteAsync(options)"
    />

    <document-generate-dialog
      v-if="worksheetGenerateDialogVisible"
      v-model:visible="worksheetGenerateDialogVisible"
      :generate-document-type="generateDocumentType"
      :generating-document-type="portalStore.documentGeneratingEnum.QUOTE"
      :generate-function="garageStore.generateWorksheetAsync"
      :next-id-function="() => garageStore.api.getWorksheetNextIdByDataAsync(refDocument.series)"
      :selected-item="refDocument"
      :form-editors="formEditors"
      @created="onDocumentGenerated"
      @cancel="refDocument = null"
    />

    <dx-splitter
      orientation="vertical"
      height="90vh"
      @resize-end="e => onSplitterResize(e, mainSplitterId)"
      @item-collapsed="e => onSplitterResize(e, mainSplitterId)"
      @initialized="e => restoreSplitterSize(e, mainSplitterId)"
    >
      <dx-item
        min-size="21%"
        :collapsible="true"
        collapsed-size="21%"
      >
        <dx-data-grid
          ref="gridRef"
          v-bind="gridOptions"
        >
          <template #attachmentsCellTemplate="{ data: cellInfo }">
            <attachment-icon-list
              :data="cellInfo.data"
              document-name="common.quote"
              :has-attachments="cellInfo.data?.has_attachments"
              :document-field="'attachment_quote'"
              :form-data-source="{
                attachment_category: portalStore.attachmentCategoryEnum.GARAGE_DOCUMENT,
              }"
              :entities-id="getAttachmentEntities(cellInfo.data, ['quotes', 'vehicle', 'subject'])"
              :refresh-grid-async="() => refreshGridAsync()"
            />
          </template>
          <template #gridLayoutConfigTemplate>
            <grid-layout-config-template
              :grid-ext-ref="gridRef"
              :grid-ext-id="gridId"
            />
          </template>
          <template #generic-caption-template="{ data }">
            <div class="dx-form-group-caption-wrapper">
              <q-icon
                :name="data.name"
                class="dx-form-group-caption-icon"
              />
              <span class="dx-form-group-caption-text">
                {{ data.caption }}
              </span>
            </div>
          </template>
          <template #worksheetRef="{ data: cellInfo }">
            <reference-link
              v-if="cellInfo.data.has_worksheet"
              :data="{
                series: cellInfo.data.worksheet?.series,
                number: cellInfo.data.worksheet?.number_mnemonic
              }"
              :text-fields="['prefix', 'series', 'number']"
              :query-fields="['series', 'number']"
              :target="menuRoutesEnum.WORKSHEETS"
              :fetch-document-relations="() => garageStore.api.getQuoteDocumentRelationTreeAsync(cellInfo.data.id)"
            >
              <information-tool-tip :data="extractReferenceData({ ...cellInfo.data.worksheet, type: portalStore.documentTypesEnum.GARAGE_WORKSHEET })" />
            </reference-link>
          </template>
          <template #fieldInfoTooltipTemplate>
            <info-tooltip-icon
              :visible="!gridRef?.instance.getVisibleRows().find(row => row.isEditing)?.data?.vehicle?.plate_number && !gridRef?.instance.getVisibleRows().find(row => row.isEditing)?.data?.vehicle?.vin"
              :message="i18n.t('common.plateVinHelpInfo')"
            />
          </template>
          <template #serviceHistoryRef="{ data: cellInfo }">
            <reference-link
              v-if="cellInfo.data.has_worksheet"
              :data="{
                prefix: cellInfo.data.chassis_code,
                vin: cellInfo.data.chassis_code,
              }"
              :text-fields="['prefix']"
              :query-fields="['vin']"
              :target="menuRoutesEnum.SERVICE_HISTORY"
            >
              <template #fieldInfoTooltipTemplate>
                <info-tooltip-icon
                  :visible="!cellInfo.data?.vehicle?.plate_number && !cellInfo.data?.vehicle?.vin"
                  :message="i18n.t('common.plateVinHelpInfo')"
                />
              </template>
            </reference-link>
          </template>
          <template #vehicleEditor="{ data: cellInfo }">
            <vehicle-drop-down-box
              show-clear-button
              :data="cellInfo"
              :disabled="isReadOnly || cellInfo.data?.rows_count > 0"
              @vue:mounted="vehicleSetValue = cellInfo.setValue"
              @value-changed="onVehicleChanged($event, cellInfo)"
            />
          </template>
          <template #customerEditor="{ data: cellInfo }">
            <customer-drop-down-box
              show-clear-button
              :data="cellInfo"
              :read-only="cellInfo.data?.has_worksheet"
              :disabled="isReadOnly || cellInfo.data?.rows_count > 0"
            />
          </template>
          <template #countryEditor="{ data: cellInfo }">
            <country-drop-down-box
              :data="cellInfo"
              :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
              :disabled="isReadOnly || !!cellInfo.data.customer?.id || cellInfo.data?.has_worksheet"
              @value-changed="onCountryChanged($event, cellInfo.column?.dataField)"
            />
          </template>
          <template #vatCountryEditor="{ data: cellInfo }">
            <country-drop-down-box
              :data="cellInfo"
              :is-portal="['customer.vat_country_id'].includes(cellInfo.column?.dataField)"
              :disabled="isReadOnly || !!cellInfo.data.customer?.id || cellInfo.data?.has_worksheet"
              @value-changed="onCountryChanged($event, cellInfo.column?.dataField, type='vat_country')"
            />
          </template>
          <template #validationTabTemplate="{data: templateOptions}">
            <validation-tab
              :title="templateOptions.title"
              :has-error="validationTabIndexErrors.includes(templateOptions.tabIndex)"
            />
          </template>
          <template #stateEditor="{ data: cellInfo }">
            <state-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.customer?.address?.state"
              :value-text="cellInfo.data.customer?.address?.state_text"
              :country="currentCountryId"
              :disabled="!!cellInfo.data.customer?.id || cellInfo.data?.has_worksheet"
              @value-changed="onStateChanged($event)"
            />
          </template>
          <template #cityEditor="{ data: cellInfo }">
            <city-drop-down-box
              :data="cellInfo"
              :value="cellInfo.data.customer?.address?.city"
              :value-text="cellInfo.data.customer?.address?.city_text"
              :country="currentCountryId"
              :state="currentStateId"
              :zip-code="currentZipCode"
              :filtered-cities-count="currentFilteredCitiesCount"
              :disabled="!!cellInfo.data.customer?.id || cellInfo.data?.has_worksheet"
            />
          </template>
          <template #depositEditor="{ data: cellInfo }">
            <deposit-drop-down-box
              :data="cellInfo"
              :read-only="cellInfo.data?.has_worksheet"
              :disabled="isReadOnly || cellInfo.data?.rows_count > 0"
            />
          </template>
          <template #mechanicsEditor="{ data }">
            <mechanic-drop-down-box
              :data="data"
              :disabled="isReadOnly || data.data?.has_worksheet"
            />
          </template>
          <template #mechanicsAvatarList="{ data }">
            <MechanicsAvatarList :ids="data.value" />
          </template>
          <template #owner-info-caption-template="{ data }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="data.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ data.caption }}
                </span>
              </div>
              <div>
                <q-btn
                  icon="mdi-account-switch"
                  flat
                  size="xs"
                  round
                  :disable="isChangeOwnerDisabled"
                  @click="addNewVehicleOwner"
                />
                <q-btn
                  icon="mdi-content-save"
                  flat
                  size="xs"
                  round
                  :disable="isOwnerSaveDisabled"
                  @click="saveNewOwner"
                />
              </div>
            </div>
          </template>
          <template #vehicle-infoBike-search-caption-template="{ data }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="data.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ i18n.t('common.infoBikeSearch', {
                    credits: garageStore.infoBikePlateSearchCredit?.credits ?? 0
                  }) }}
                </span>
              </div>
              <div>
                <info-bike-search-caption-button
                  :disabled="infoBikeSearchDisabled"
                  :show-pulse="showPulse"
                  :pulse-count="pulseCount"
                  :tooltip="i18n.t('common.infoBike')"
                  @search="infoBikeSearch"
                  @pulse-end="showPulse = false"
                />
              </div>
            </div>
          </template>
          <template #infoBikePlateNumberEditor="{ data }">
            <info-bike-plate-number-editor
              :cell-info="data"
              :readonly="isReadOnly || data.data?.rows_count > 0 || !!data.data?.vehicle?.id"
              @update="v => data.setValue(v)"
              @blur="() => onPlateBlur()"
            />
          </template>
          <template #vehicle-info-caption-template="{ data }">
            <div
              class="dx-form-group-caption"
              style="display: flex; align-items: center; justify-content: space-between; width: 100%;"
            >
              <div class="dx-form-group-caption-wrapper">
                <q-icon
                  :name="data.name"
                  class="dx-form-group-caption-icon"
                />
                <span class="dx-form-group-caption-text">
                  {{ data.caption }}
                </span>
              </div>
              <div>
                <q-btn
                  icon="mdi-content-save"
                  flat
                  size="xs"
                  round
                  :disable="isVehicleSaveDisabled"
                  @click="saveNewVehicle"
                >
                  <q-tooltip>
                    {{ i18n.t('common.fill_vehicle_fields_and_owner') }}
                  </q-tooltip>
                </q-btn>
              </div>
            </div>
          </template>
          <template #tagsEditor>
            <document-tags-tag-box
              :tags="allComputedTags"
              :value="allComputedTags.map(x => x.id)"
              disabled
            />
          </template>
          <template
            #editButtonsTemplate="{ data: cellInfo }"
          >
            <edit-lock-buttons-template
              :entity-name="'Quote'"
              :cell-info="cellInfo"
              :grid="gridRef"
              :entity-lock="entityLock"
              :is-locked-available="true"
              :buttons="rowButtons"
            />
          </template>
          <template #customToolbarAddButtonTemplate>
            <custom-toolbar-add-button-template
              :grid="gridRef"
              :entity-lock="entityLock"
            />
          </template>
          <template #customToolbarDeleteButtonTemplate>
            <custom-toolbar-delete-button-template
              :selected-rows-id="selectedRows?.map(i => i.id) || []"
              :grid="gridRef"
              :entity-lock="entityLock"
              :on-delete="batchDeleteAsync"
              :disabled="selectedRows?.some(row => row.worksheet || row.has_rounding_expense_row || row.has_rounding_row) || selectedRows.length === 0"
            />
          </template>
          <template #carServiceNumberEditor="{ data: cellInfo }">
            <car-service-number-box
              :data="cellInfo"
              :value="cellInfo.value"
              :disabled="isReadOnly ||cellInfo.data?.rows_count > 0"
              :garage-reason-ref="cellInfo.data.garage_reason_ref"
              :current-car-service="currentCarService"
            />
          </template>
        </dx-data-grid>
      </dx-item>
      <dx-item
        :collapsible="true"
        min-size="50%"
      >
        <div style="height: 100%; width: 100%;">
          <quote-row-grid
            v-if="selectedDocument"
            :data="selectedDocument"
            :read-only="selectedDocument.has_worksheet || (entityLock.isLocked(false, selectedDocument.id) && !entityLock.isLocked(true, selectedDocument.id)) || selectedDocument.has_rounding_row"
            :entity-lock="entityLock"
            @row-saved="onDetailRowSaved"
            @content-ready="onDetailContentReady"
          />
          <div
            v-else
            style="min-height: 1px;"
          />
        </div>
      </dx-item>
    </dx-splitter>
  </div>
</template>

<style scoped lang="scss">
.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
</style>
